import {
    AttendanceSessionId,
    IAttendanceAdditionalSessions,
    IAttendanceConfiguration,
    IAttendanceConsumerConfiguration,
    IAttendanceSessionDefinition,
    IAttendanceSessionPair,
    IAttendanceSessionSettings,
} from "../../../../models/AttendanceConfiguration";
import { dfeAttendanceCodeDefinitions } from "../../../../app/common/data/AttendanceCodeCatalogue";
import { legacyAttendanceSessionDefinitions } from "../../../../app/common/data/AttendanceSessionDefinitions";

const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;
const codePattern = /^[A-Z0-9!#?^~*+\-/\\]{1,2}$/;
const requiredSessionIds = ["am", "pm"] as const;

function copySessionSettings(settings: IAttendanceSessionSettings): IAttendanceSessionSettings {
    return {
        ...settings,
        timingBands: settings.timingBands ? { ...settings.timingBands } : undefined,
        providerSessionDurationHintMinutes: calculateProviderSessionDurationHintMinutes(
            settings.registrationStart,
            settings.providerSessionEnd,
        ),
    };
}

export function copySessionPair(settings: IAttendanceSessionPair): IAttendanceSessionPair {
    return {
        am: copySessionSettings(settings.am),
        pm: copySessionSettings(settings.pm),
    };
}

function copyAdditionalSessions(sessions: IAttendanceAdditionalSessions | undefined): IAttendanceAdditionalSessions {
    const copiedSessions: IAttendanceAdditionalSessions = {};

    Object.entries(sessions ?? {}).forEach(([sessionId, settings]) => {
        copiedSessions[sessionId] = copySessionSettings(settings);
    });

    return copiedSessions;
}

export function getAttendanceSessionDefinitions(
    configuration: IAttendanceConfiguration,
): readonly IAttendanceSessionDefinition[] {
    return configuration.sessionDefinitions && configuration.sessionDefinitions.length > 0
        ? configuration.sessionDefinitions
        : legacyAttendanceSessionDefinitions;
}

export function getDefaultSessionSettings(
    configuration: IAttendanceConfiguration,
    sessionId: AttendanceSessionId,
): IAttendanceSessionSettings | undefined {
    if (sessionId === "am") {
        return configuration.defaultSessions.am;
    }

    if (sessionId === "pm") {
        return configuration.defaultSessions.pm;
    }

    return configuration.additionalDefaultSessions?.[sessionId];
}

function getYearGroupSessionSettings(
    configuration: IAttendanceConfiguration,
    yearGroup: string,
    sessionId: AttendanceSessionId,
): IAttendanceSessionSettings | undefined {
    const settings = configuration.yearGroupSettings.find((item) => item.yearGroup === yearGroup);

    if (!settings) {
        return undefined;
    }

    if (sessionId === "am") {
        return settings.am;
    }

    if (sessionId === "pm") {
        return settings.pm;
    }

    return settings.additionalSessions?.[sessionId];
}

export function getOverriddenSessionIds(
    configuration: IAttendanceConfiguration,
    yearGroup: string,
): AttendanceSessionId[] {
    const settings = configuration.yearGroupSettings.find((item) => item.yearGroup === yearGroup);

    if (!settings || settings.usesDefault) {
        return [];
    }

    const availableSessionIds = new Set(getAttendanceSessionDefinitions(configuration).map((definition) => definition.id));
    const overriddenSessionIds = settings.overriddenSessionIds
        ?? getAttendanceSessionDefinitions(configuration).map((definition) => definition.id);

    return Array.from(new Set(overriddenSessionIds)).filter((sessionId) => availableSessionIds.has(sessionId));
}

export function getEffectiveSessionSettings(
    configuration: IAttendanceConfiguration,
    yearGroup: string | null,
    sessionId: AttendanceSessionId,
): IAttendanceSessionSettings | undefined {
    const defaultSettings = getDefaultSessionSettings(configuration, sessionId);

    if (!yearGroup || !getOverriddenSessionIds(configuration, yearGroup).includes(sessionId)) {
        return defaultSettings;
    }

    return getYearGroupSessionSettings(configuration, yearGroup, sessionId) ?? defaultSettings;
}

function createAdditionalSessionSettings(
    registrationStart: string,
    providerSessionEnd: string,
): IAttendanceSessionSettings {
    return {
        registrationStart,
        timetableStart: registrationStart,
        providerSessionEnd,
        providerSessionDurationHintMinutes: calculateProviderSessionDurationHintMinutes(
            registrationStart,
            providerSessionEnd,
        ),
    };
}

export function getNextAdditionalSessionId(configuration: IAttendanceConfiguration): AttendanceSessionId {
    const existingIds = new Set(getAttendanceSessionDefinitions(configuration).map((definition) => definition.id));
    let sequence = 1;

    while (existingIds.has(`session-${sequence}`)) {
        sequence += 1;
    }

    return `session-${sequence}`;
}

export function addAttendanceSession(
    configuration: IAttendanceConfiguration,
    sessionId: AttendanceSessionId,
    label: string,
    registrationStart: string,
    providerSessionEnd: string,
    afterSessionId: AttendanceSessionId,
): IAttendanceConfiguration {
    const sessionDefinitions = [...getAttendanceSessionDefinitions(configuration)];

    if (sessionDefinitions.some((definition) => definition.id === sessionId)) {
        return configuration;
    }

    const insertAfterIndex = sessionDefinitions.findIndex((definition) => definition.id === afterSessionId);
    const newSession = {
        id: sessionId,
        label: label.trim(),
        shortLabel: label.trim(),
        kind: "session" as const,
    };
    const insertIndex = insertAfterIndex >= 0 ? insertAfterIndex + 1 : sessionDefinitions.length;

    return {
        ...configuration,
        sessionDefinitions: [
            ...sessionDefinitions.slice(0, insertIndex),
            newSession,
            ...sessionDefinitions.slice(insertIndex),
        ],
        additionalDefaultSessions: {
            ...configuration.additionalDefaultSessions,
            [sessionId]: createAdditionalSessionSettings(registrationStart, providerSessionEnd),
        },
    };
}

export function updateAttendanceSessionLabel(
    configuration: IAttendanceConfiguration,
    sessionId: AttendanceSessionId,
    label: string,
): IAttendanceConfiguration {
    if (requiredSessionIds.includes(sessionId as typeof requiredSessionIds[number])) {
        return configuration;
    }

    const sessionDefinitions = getAttendanceSessionDefinitions(configuration);
    const hasSession = sessionDefinitions.some((definition) => definition.id === sessionId);

    if (!hasSession) {
        return configuration;
    }

    return {
        ...configuration,
        sessionDefinitions: sessionDefinitions.map((definition) => (
            definition.id === sessionId
                ? { ...definition, label, shortLabel: label.trim() }
                : definition
        )),
    };
}

function removeAdditionalSessionSettings(
    sessions: IAttendanceAdditionalSessions | undefined,
    sessionId: AttendanceSessionId,
): IAttendanceAdditionalSessions | undefined {
    if (!sessions || !Object.prototype.hasOwnProperty.call(sessions, sessionId)) {
        return sessions;
    }

    const { [sessionId]: removedSession, ...remainingSessions } = sessions;
    return remainingSessions;
}

export function removeAttendanceSession(
    configuration: IAttendanceConfiguration,
    sessionId: AttendanceSessionId,
): IAttendanceConfiguration {
    if (requiredSessionIds.includes(sessionId as typeof requiredSessionIds[number])) {
        return configuration;
    }

    const sessionDefinitions = getAttendanceSessionDefinitions(configuration);
    const hasSession = sessionDefinitions.some((definition) => definition.id === sessionId);

    if (!hasSession) {
        return configuration;
    }

    return {
        ...configuration,
        sessionDefinitions: sessionDefinitions.filter((definition) => definition.id !== sessionId),
        additionalDefaultSessions: removeAdditionalSessionSettings(
            configuration.additionalDefaultSessions,
            sessionId,
        ),
        yearGroupSettings: configuration.yearGroupSettings.map((settings) => {
            const overriddenSessionIds = getOverriddenSessionIds(configuration, settings.yearGroup)
                .filter((overriddenSessionId) => overriddenSessionId !== sessionId);

            return {
                ...settings,
                additionalSessions: removeAdditionalSessionSettings(settings.additionalSessions, sessionId),
                overriddenSessionIds,
                usesDefault: overriddenSessionIds.length === 0,
            };
        }),
    };
}

export function normaliseAttendanceCodes(values: string[]): string[] {
    const normalisedCodes = new Set<string>();

    values.forEach((value) => {
        const code = value.trim().toUpperCase();

        if (code.length > 0) {
            normalisedCodes.add(code);
        }
    });

    return Array.from(normalisedCodes);
}

export function getProtectedAttendanceCodes(
    customCodes: string[],
    overwritableMarks: string[],
): string[] {
    const overwriteExceptions = new Set(normaliseAttendanceCodes(overwritableMarks));

    return normaliseAttendanceCodes([
        ...dfeAttendanceCodeDefinitions.map((definition) => definition.code),
        ...customCodes,
    ]).filter((code) => !overwriteExceptions.has(code));
}

export function isValidAttendanceCode(value: string): boolean {
    return codePattern.test(value);
}

export function getEffectiveSessionPair(
    configuration: IAttendanceConfiguration,
    yearGroup: string,
): IAttendanceSessionPair {
    return {
        am: getEffectiveSessionSettings(configuration, yearGroup, "am") ?? configuration.defaultSessions.am,
        pm: getEffectiveSessionSettings(configuration, yearGroup, "pm") ?? configuration.defaultSessions.pm,
    };
}

function getEffectiveAdditionalSessions(
    configuration: IAttendanceConfiguration,
    yearGroup: string,
): IAttendanceAdditionalSessions {
    const additionalSessions: IAttendanceAdditionalSessions = {};

    getAttendanceSessionDefinitions(configuration).forEach((definition) => {
        if (definition.kind === "registration") {
            return;
        }

        const settings = getEffectiveSessionSettings(configuration, yearGroup, definition.id);
        if (settings) {
            additionalSessions[definition.id] = settings;
        }
    });

    return additionalSessions;
}

export function copyYearGroupSettings(
    configuration: IAttendanceConfiguration,
    sourceYearGroup: string,
    targetYearGroups: string[],
): IAttendanceConfiguration {
    const source = configuration.yearGroupSettings.find((settings) => settings.yearGroup === sourceYearGroup);

    if (!source) {
        return configuration;
    }

    const targetYearGroupSet = new Set(targetYearGroups);
    const sessionDefinitions = getAttendanceSessionDefinitions(configuration);
    const sourceSessions = getEffectiveSessionPair(configuration, sourceYearGroup);
    const sourceAdditionalSessions = getEffectiveAdditionalSessions(configuration, sourceYearGroup);

    return {
        ...configuration,
        yearGroupSettings: configuration.yearGroupSettings.map((settings) => {
            if (!targetYearGroupSet.has(settings.yearGroup) || settings.yearGroup === sourceYearGroup) {
                return settings;
            }

            return {
                yearGroup: settings.yearGroup,
                usesDefault: false,
                overriddenSessionIds: sessionDefinitions.map((definition) => definition.id),
                ...copySessionPair(sourceSessions),
                additionalSessions: copyAdditionalSessions(sourceAdditionalSessions),
            };
        }),
    };
}

function toMinutes(value: string): number {
    const [hour, minute] = value.split(":").map(Number);
    return (hour * 60) + minute;
}

export function toIsoDateFromUkDate(value: string): string | null {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);

    if (!match) {
        return null;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (date.getUTCFullYear() !== year
        || date.getUTCMonth() !== month - 1
        || date.getUTCDate() !== day) {
        return null;
    }

    return `${match[3]}-${match[2]}-${match[1]}`;
}

export function toUkDateFromIsoDate(value: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

    if (!match) {
        return "";
    }

    const ukDate = `${match[3]}/${match[2]}/${match[1]}`;

    return toIsoDateFromUkDate(ukDate) === value ? ukDate : "";
}

export function calculateProviderSessionDurationHintMinutes(
    registrationStart: string,
    providerSessionEnd: string,
): number | null {
    if (!timePattern.test(registrationStart) || !timePattern.test(providerSessionEnd)) {
        return null;
    }

    const startMinutes = toMinutes(registrationStart);
    const endMinutes = toMinutes(providerSessionEnd);

    return endMinutes > startMinutes ? endMinutes - startMinutes : null;
}

export function synchroniseProviderSessionDurationHints(
    configuration: IAttendanceConfiguration,
): IAttendanceConfiguration {
    return {
        ...configuration,
        defaultSessions: copySessionPair(configuration.defaultSessions),
        additionalDefaultSessions: copyAdditionalSessions(configuration.additionalDefaultSessions),
        yearGroupSettings: configuration.yearGroupSettings.map((settings) => ({
            ...settings,
            ...copySessionPair(settings),
            additionalSessions: copyAdditionalSessions(settings.additionalSessions),
            overriddenSessionIds: settings.overriddenSessionIds ? [...settings.overriddenSessionIds] : undefined,
        })),
    };
}

function validateSessionSettings(
    label: string,
    settings: IAttendanceSessionSettings,
    requiresTimingBands: boolean,
): string[] {
    const errors: string[] = [];

    if (!timePattern.test(settings.registrationStart)) {
        errors.push(`${label}: registration start must be a valid time.`);
    }

    if (!timePattern.test(settings.providerSessionEnd)) {
        errors.push(`${label}: provider session end must be a valid time.`);
    }

    if (timePattern.test(settings.registrationStart)
        && timePattern.test(settings.providerSessionEnd)
        && toMinutes(settings.providerSessionEnd) <= toMinutes(settings.registrationStart)) {
        errors.push(`${label}: provider session end must be after registration start.`);
    }

    if (!requiresTimingBands) {
        return errors;
    }

    if (!settings.timingBands) {
        errors.push(`${label}: arrival timing bands are required.`);
        return errors;
    }

    const {
        preSessionGraceMinutes,
        onTimeGraceMinutes,
        earlyLateBandMinutes,
        lateLateBandMinutes,
    } = settings.timingBands;

    if (!Number.isInteger(preSessionGraceMinutes) || preSessionGraceMinutes < 0) {
        errors.push(`${label}: pre-session grace must be a whole number of zero or more minutes.`);
    }

    if (!Number.isInteger(onTimeGraceMinutes) || onTimeGraceMinutes < 0) {
        errors.push(`${label}: on-time grace must be a whole number of zero or more minutes.`);
    }

    if (!Number.isInteger(earlyLateBandMinutes) || earlyLateBandMinutes < 0) {
        errors.push(`${label}: early-late band must be a whole number of zero or more minutes.`);
    }

    if (!Number.isInteger(lateLateBandMinutes) || lateLateBandMinutes < 0) {
        errors.push(`${label}: late-late band must be a whole number of zero or more minutes.`);
    }

    return errors;
}

function getSessionValidationLabel(definition: IAttendanceSessionDefinition): string {
    if (definition.id === "am") {
        return "AM session";
    }

    if (definition.id === "pm") {
        return "PM session";
    }

    return definition.label;
}

function containsDuplicateCodes(values: string[]): boolean {
    return normaliseAttendanceCodes(values).length !== values.filter((value) => value.trim().length > 0).length;
}

export function validateAttendanceConfiguration(
    configuration: IAttendanceConfiguration,
    today: string,
): string[] {
    const errors: string[] = [];
    const sessionDefinitions = getAttendanceSessionDefinitions(configuration);
    const definedSessionIds = new Set<string>();
    const definedSessionLabels = new Set<string>();

    sessionDefinitions.forEach((definition) => {
        if (definition.id.trim().length === 0 || definition.label.trim().length === 0) {
            errors.push("Every session must have an identifier and label.");
            return;
        }

        if (definedSessionIds.has(definition.id)) {
            errors.push(`Session ${definition.label} is configured more than once.`);
            return;
        }

        definedSessionIds.add(definition.id);
        const normalisedLabel = definition.label.trim().toLocaleLowerCase();
        if (definedSessionLabels.has(normalisedLabel)) {
            errors.push(`Session ${definition.label} is configured more than once.`);
            return;
        }
        definedSessionLabels.add(normalisedLabel);
        const settings = getDefaultSessionSettings(configuration, definition.id);

        if (!settings) {
            errors.push(`Default ${getSessionValidationLabel(definition)} settings are required.`);
            return;
        }

        errors.push(...validateSessionSettings(
            `Default ${getSessionValidationLabel(definition)}`,
            settings,
            definition.kind === "registration",
        ));
    });

    requiredSessionIds.forEach((sessionId) => {
        const definition = sessionDefinitions.find((item) => item.id === sessionId);

        if (!definition || !definedSessionIds.has(sessionId)) {
            errors.push(`${sessionId.toUpperCase()} registration is required.`);
        } else if (definition.kind !== "registration") {
            errors.push(`${sessionId.toUpperCase()} registration must be a registration session.`);
        }
    });

    configuration.yearGroupSettings.forEach((settings) => {
        getOverriddenSessionIds(configuration, settings.yearGroup).forEach((sessionId) => {
            const definition = sessionDefinitions.find((item) => item.id === sessionId);
            const sessionSettings = getYearGroupSessionSettings(configuration, settings.yearGroup, sessionId);

            if (definition && sessionSettings) {
                errors.push(...validateSessionSettings(
                    `${settings.yearGroup} ${getSessionValidationLabel(definition)}`,
                    sessionSettings,
                    definition.kind === "registration",
                ));
            }
        });
    });

    if (containsDuplicateCodes(configuration.catalogue.dfeCodes)
        || containsDuplicateCodes(configuration.catalogue.customCodes)
        || containsDuplicateCodes(configuration.protectedCodes)
        || containsDuplicateCodes(configuration.overwritableMarks)) {
        errors.push("Attendance codes and marks must not contain duplicates.");
    }

    configuration.catalogue.customCodes.forEach((code) => {
        if (!isValidAttendanceCode(code)) {
            errors.push(`Custom code ${code} must contain one or two supported non-whitespace characters.`);
        }
    });

    if (!/^\d{4}-\d{2}-\d{2}$/.test(configuration.operations.liveDate)
        || configuration.operations.liveDate < today) {
        errors.push("LiveDate must be today or a future date.");
    }

    if (!Number.isInteger(configuration.operations.operationalMarkRetentionDays)
        || configuration.operations.operationalMarkRetentionDays < 1) {
        errors.push("Operational mark retention must be at least one day.");
    }

    if (!Number.isInteger(configuration.operations.commandHistoryRetentionDays)
        || configuration.operations.commandHistoryRetentionDays < 1) {
        errors.push("Command history retention must be at least one day.");
    }

    if (!timePattern.test(configuration.operations.dailySyncTime)) {
        errors.push("Attendance schedule must be a valid time.");
    }

    return errors;
}

export function getChangedSections(
    previous: IAttendanceConfiguration | null,
    next: IAttendanceConfiguration,
): string[] {
    if (!previous) {
        return [
            "Session timing",
            "Code catalogue",
        ];
    }

    const sections: Array<{ label: string; beforeValue: unknown; afterValue: unknown }> = [
        {
            label: "Session timing",
            beforeValue: {
                defaultSessions: previous.defaultSessions,
                yearGroupSettings: previous.yearGroupSettings,
            },
            afterValue: {
                defaultSessions: next.defaultSessions,
                yearGroupSettings: next.yearGroupSettings,
            },
        },
        {
            label: "Code catalogue",
            beforeValue: {
                catalogue: previous.catalogue,
                overwritableMarks: previous.overwritableMarks,
            },
            afterValue: {
                catalogue: next.catalogue,
                overwritableMarks: next.overwritableMarks,
            },
        },
    ];

    return sections
        .filter((section) => JSON.stringify(section.beforeValue) !== JSON.stringify(section.afterValue))
        .map((section) => section.label);
}

export function toConsumerConfiguration(
    configuration: IAttendanceConfiguration,
): IAttendanceConsumerConfiguration {
    return {
        apiVersion: "v1",
        establishmentId: configuration.establishmentId,
        sessionDefinitions: getAttendanceSessionDefinitions(configuration).map((definition) => ({ ...definition })),
        defaultSessions: copySessionPair(configuration.defaultSessions),
        additionalDefaultSessions: copyAdditionalSessions(configuration.additionalDefaultSessions),
        yearGroupOverrides: configuration.yearGroupSettings.map((settings) => ({
            yearGroup: settings.yearGroup,
            usesDefault: settings.usesDefault,
            ...copySessionPair(settings),
            additionalSessions: copyAdditionalSessions(settings.additionalSessions),
            overriddenSessionIds: settings.overriddenSessionIds ? [...settings.overriddenSessionIds] : undefined,
        })),
        codeCatalogue: normaliseAttendanceCodes([
            ...configuration.catalogue.dfeCodes,
            ...configuration.catalogue.customCodes,
        ]),
        protectedCodes: normaliseAttendanceCodes(configuration.protectedCodes),
        overwritableMarks: normaliseAttendanceCodes(configuration.overwritableMarks),
        liveDate: configuration.operations.liveDate,
        operationalSettings: {
            operationalMarkRetentionDays: configuration.operations.operationalMarkRetentionDays,
            commandHistoryRetentionDays: configuration.operations.commandHistoryRetentionDays,
            dailySyncTime: configuration.operations.dailySyncTime,
        },
    };
}